import React, { useState, useEffect, CSSProperties } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { RootState } from "../../../redux/store";
import axios from "axios";

/* ─── PALETTE — matches OrganizationOverview exactly ─── */
const IVORY    = "#F5F0EB";
const CREAM    = "#EDE8E1";
const STONE    = "#D4CFC8";
const WARMGRAY = "#9B958D";
const DUSK     = "#6B6560";
const NIGHT    = "#1A1714";
const PURE_W   = "#FFFFFF";
const PURE_B   = "#000000";
const ROUGE    = "#C8303A";
const SAGE     = "#4A7C59";
const AMBER    = "#B8860B";
const GOLD     = "#C9A84C";

/* ─── FONT INJECTION ─── */
const fontHref =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Libre+Franklin:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const link = document.createElement("link");
  link.rel  = "stylesheet";
  link.href = fontHref;
  document.head.appendChild(link);
}

/* ─── STYLES ─── */
const serif: CSSProperties = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const sans: CSSProperties  = { fontFamily: "'Libre Franklin', 'Helvetica Neue', sans-serif" };
const mono: CSSProperties  = { fontFamily: "'Courier New', monospace" };

/* ─── ANIMATIONS ─── */
const fadeUp: Variants = {
  hidden:  { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] },
  }),
};

const modalOverlay: Variants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1 },
  exit:    { opacity: 0 },
};

const modalContent: Variants = {
  hidden:  { opacity: 0, scale: 0.94, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
  exit:    { opacity: 0, scale: 0.96, y: 10 },
};

/* ─── CURRENCY DATA — all currencies with defaults ─── */
const ALL_CURRENCIES = [
  { code: "USD", symbol: "$",      name: "US Dollar",                  default: 1 },
  { code: "EUR", symbol: "€",      name: "Euro",                       default: 0.92 },
  { code: "GBP", symbol: "£",      name: "British Pound",              default: 0.79 },
  { code: "JPY", symbol: "¥",      name: "Japanese Yen",               default: 151.20 },
  { code: "AUD", symbol: "A$",     name: "Australian Dollar",          default: 1.53 },
  { code: "CAD", symbol: "C$",     name: "Canadian Dollar",            default: 1.37 },
  { code: "CHF", symbol: "CHF",    name: "Swiss Franc",                default: 0.90 },
  { code: "CNY", symbol: "¥",      name: "Chinese Yuan",               default: 7.24 },
  { code: "INR", symbol: "₹",      name: "Indian Rupee",               default: 83.50 },
  { code: "RUB", symbol: "₽",      name: "Russian Ruble",              default: 91.50 },
  { code: "BRL", symbol: "R$",     name: "Brazilian Real",             default: 5.05 },
  { code: "ZAR", symbol: "R",      name: "South African Rand",         default: 18.65 },
  { code: "KRW", symbol: "₩",      name: "South Korean Won",           default: 1345 },
  { code: "MXN", symbol: "$",      name: "Mexican Peso",               default: 17.20 },
  { code: "SEK", symbol: "kr",     name: "Swedish Krona",              default: 10.45 },
  { code: "SGD", symbol: "S$",     name: "Singapore Dollar",           default: 1.34 },
  { code: "TRY", symbol: "₺",      name: "Turkish Lira",               default: 32.10 },
  { code: "NGN", symbol: "₦",      name: "Nigerian Naira",             default: 1580 },
  { code: "EGP", symbol: "£",      name: "Egyptian Pound",             default: 48.50 },
  { code: "PHP", symbol: "₱",      name: "Philippine Peso",            default: 56.80 },
  { code: "AED", symbol: "د.إ",    name: "UAE Dirham",                 default: 3.67 },
  { code: "KES", symbol: "KSh",    name: "Kenyan Shilling",            default: 132 },
  { code: "GHS", symbol: "₵",      name: "Ghanaian Cedi",              default: 15.50 },
  { code: "TZS", symbol: "TSh",    name: "Tanzanian Shilling",         default: 2545 },
  { code: "UGX", symbol: "USh",    name: "Ugandan Shilling",           default: 3800 },
  { code: "RWF", symbol: "FRw",    name: "Rwandan Franc",              default: 1310 },
  { code: "ZMW", symbol: "ZK",     name: "Zambian Kwacha",             default: 26.80 },
  { code: "MWK", symbol: "MK",     name: "Malawian Kwacha",            default: 1730 },
  { code: "XOF", symbol: "CFA",    name: "CFA Franc BCEAO",            default: 604 },
  { code: "XAF", symbol: "FCFA",   name: "CFA Franc BEAC",             default: 604 },
  { code: "MAD", symbol: "د.م.",   name: "Moroccan Dirham",            default: 10.00 },
  { code: "ETB", symbol: "Br",     name: "Ethiopian Birr",             default: 57.50 },
  { code: "BIF", symbol: "FBu",    name: "Burundian Franc",            default: 2870 },
  { code: "DKK", symbol: "kr",     name: "Danish Krone",               default: 6.87 },
  { code: "NOK", symbol: "kr",     name: "Norwegian Krone",            default: 10.60 },
  { code: "PLN", symbol: "zł",     name: "Polish Zloty",               default: 3.97 },
  { code: "CZK", symbol: "Kč",     name: "Czech Koruna",               default: 23.20 },
  { code: "HUF", symbol: "Ft",     name: "Hungarian Forint",           default: 361 },
  { code: "RON", symbol: "lei",    name: "Romanian Leu",               default: 4.58 },
  { code: "HKD", symbol: "$",      name: "Hong Kong Dollar",           default: 7.82 },
  { code: "NZD", symbol: "$",      name: "New Zealand Dollar",         default: 1.64 },
  { code: "SAR", symbol: "ر.س",   name: "Saudi Riyal",                default: 3.75 },
  { code: "QAR", symbol: "ر.ق",   name: "Qatari Rial",                default: 3.64 },
  { code: "KWD", symbol: "د.ك",   name: "Kuwaiti Dinar",              default: 0.307 },
  { code: "BHD", symbol: ".د.ب",  name: "Bahraini Dinar",             default: 0.376 },
  { code: "PKR", symbol: "₨",      name: "Pakistani Rupee",            default: 278 },
  { code: "BDT", symbol: "৳",      name: "Bangladeshi Taka",           default: 110 },
  { code: "IDR", symbol: "Rp",     name: "Indonesian Rupiah",          default: 15750 },
  { code: "MYR", symbol: "RM",     name: "Malaysian Ringgit",          default: 4.72 },
  { code: "THB", symbol: "฿",      name: "Thai Baht",                  default: 35.20 },
  { code: "VND", symbol: "₫",      name: "Vietnamese Dong",            default: 25100 },
  { code: "UAH", symbol: "₴",      name: "Ukrainian Hryvnia",          default: 39.60 },
];

const CURRENCY_API = "https://europe-west3-cosmoola.cloudfunctions.net/upsertRates";

/* ══════════════════════════════════════════════════
   RATES PAGE
   ══════════════════════════════════════════════════ */
export default function Rates() {
  const navigate = useNavigate();
  const user     = useSelector((s: RootState) => s.auth.user);

  /* ── State ── */
  const [rates,      setRates]      = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    ALL_CURRENCIES.forEach(c => { init[c.code] = c.default; });
    return init;
  });
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [isMobile,   setIsMobile]   = useState(typeof window !== "undefined" ? window.innerWidth < 800 : false);
  const [confirm,    setConfirm]    = useState(false);
  const [edited,     setEdited]     = useState<Set<string>>(new Set());
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  /* ── Filter currencies ── */
  const filtered = ALL_CURRENCIES.filter(c =>
    `${c.code} ${c.name}`.toLowerCase().includes(search.toLowerCase())
  );

  /* ── Handle rate change ── */
  const handleChange = (code: string, val: string) => {
    const num = parseFloat(val);
    if (val === "" || isNaN(num)) return;
    setRates(prev => ({ ...prev, [code]: num }));
    setEdited(prev => new Set(prev).add(code));
  };

  /* ── Reset to defaults ── */
  const handleReset = () => {
    const init: Record<string, number> = {};
    ALL_CURRENCIES.forEach(c => { init[c.code] = c.default; });
    setRates(init);
    setEdited(new Set());
    setError(null);
    setSuccess(false);
  };

  /* ── Submit — no auth, plain POST ── */
  const handleSubmit = async () => {
    setConfirm(false);
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      if (!user) throw new Error("Not signed in.");

      const { data } = await axios.post(CURRENCY_API, { rates });

      setSuccess(true);
      setEdited(new Set());
      setLastUpdate(
        data.updatedAt
          ? new Date(data.updatedAt).toLocaleString()
          : new Date().toLocaleString()
      );
      setTimeout(() => setSuccess(false), 4000);
    } catch (e: any) {
      console.error("upsertRates error:", e);
      setError(e?.response?.data?.error?.message || e?.message || "Failed to update rates.");
    } finally {
      setLoading(false);
    }
  };

  const editedCount = edited.size;

  /* ══ RENDER ══ */
  return (
    <div style={{ minHeight: "100vh", background: IVORY, ...sans }}>

      {/* ── HEADER ── */}
      <div style={{
        background: PURE_B, padding: isMobile ? "28px 20px" : "36px 56px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16,
      }}>
        <div>
          <div style={{ ...serif, fontSize: isMobile ? 24 : 30, fontWeight: 300, letterSpacing: "0.28em", color: PURE_W, lineHeight: 1 }}>
            COSMOOLA
          </div>
          <div style={{ ...sans, fontSize: 9, letterSpacing: "0.35em", color: WARMGRAY, marginTop: 6, textTransform: "uppercase", fontWeight: 500 }}>
            Currency Rate Management
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
          onClick={() => navigate(-1)}
          style={{ ...sans, padding: isMobile ? "10px 16px" : "12px 28px", border: `1.5px solid ${PURE_W}`, background: "transparent", color: PURE_W, fontWeight: 600, fontSize: 11, cursor: "pointer", letterSpacing: "0.14em", textTransform: "uppercase", transition: "all 0.3s" }}
        >
          ← Back
        </motion.button>
      </div>

      {/* ── CONTENT ── */}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: isMobile ? "32px 16px" : "52px 40px" }}>

        {/* ── Page title + meta ── */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible" style={{ marginBottom: 40 }}>
          <h1 style={{ ...serif, fontSize: isMobile ? 34 : 48, fontWeight: 300, color: NIGHT, margin: "0 0 12px", letterSpacing: "-0.02em", fontStyle: "italic" }}>
            Exchange Rates
          </h1>
          <p style={{ ...sans, fontSize: 13, color: DUSK, lineHeight: 1.7, fontWeight: 300, maxWidth: 560, margin: 0 }}>
            Set the exchange rates relative to <strong style={{ fontWeight: 600 }}>USD (base)</strong>. These rates are served to all merchants using your currency converter SDK. Update daily for accurate display pricing.
          </p>
          {lastUpdate && (
            <div style={{ marginTop: 10, ...sans, fontSize: 11, color: WARMGRAY, letterSpacing: "0.04em" }}>
              Last updated: <span style={{ ...mono, color: DUSK }}>{lastUpdate}</span>
            </div>
          )}
        </motion.div>

        {/* ── Status banners ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
              style={{ marginBottom:20, padding:"14px 20px", background:`${ROUGE}08`, border:`1px solid ${ROUGE}22`, ...sans, fontSize:13, color:ROUGE }}>
              ⚠ {error}
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity:0, y:-8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
              style={{ marginBottom:20, padding:"14px 20px", background:`${SAGE}08`, border:`1px solid ${SAGE}22`, ...sans, fontSize:13, color:SAGE }}>
              ✓ {ALL_CURRENCIES.length} currencies updated successfully in Firestore.
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Toolbar ── */}
        <motion.div variants={fadeUp} custom={1} initial="hidden" animate="visible"
          style={{ display:"flex", gap:12, marginBottom:28, flexWrap:"wrap", alignItems:"center" }}>

          {/* Search */}
          <div style={{ flex:1, minWidth:200, position:"relative" }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search currency…"
              style={{ ...sans, width:"100%", boxSizing:"border-box", padding:"11px 14px 11px 36px", border:`1px solid ${search ? NIGHT : STONE}`, background:PURE_W, fontSize:13, color:NIGHT, outline:"none", transition:"border-color .2s" }}
            />
            <span style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:WARMGRAY, fontSize:13 }}>⌕</span>
          </div>

          {/* Edited badge */}
          {editedCount > 0 && (
            <div style={{ ...sans, fontSize:11, color:AMBER, border:`1px solid ${AMBER}33`, padding:"8px 14px", background:`${AMBER}08`, letterSpacing:"0.06em", fontWeight:600 }}>
              {editedCount} edited
            </div>
          )}

          {/* Reset */}
          <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:0.98 }}
            onClick={handleReset}
            style={{ ...sans, padding:"11px 20px", border:`1.5px solid ${STONE}`, background:"transparent", color:DUSK, fontWeight:600, fontSize:11, cursor:"pointer", letterSpacing:"0.12em", textTransform:"uppercase", transition:"all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=NIGHT; e.currentTarget.style.color=NIGHT; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=STONE; e.currentTarget.style.color=DUSK; }}>
            Reset Defaults
          </motion.button>

          {/* Save button */}
          <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:0.98 }}
            onClick={() => setConfirm(true)}
            disabled={loading}
            style={{ ...sans, padding:"11px 28px", border:`2px solid ${loading ? STONE : NIGHT}`, background:loading ? "#F8F6F3" : NIGHT, color:loading ? WARMGRAY : PURE_W, fontWeight:600, fontSize:11, cursor:loading?"wait":"pointer", letterSpacing:"0.16em", textTransform:"uppercase", transition:"all 0.3s" }}
            onMouseEnter={e => { if(!loading){ e.currentTarget.style.background="transparent"; e.currentTarget.style.color=NIGHT; } }}
            onMouseLeave={e => { if(!loading){ e.currentTarget.style.background=NIGHT; e.currentTarget.style.color=PURE_W; } }}>
            {loading ? "Saving…" : `Save All Rates`}
          </motion.button>
        </motion.div>

        {/* ── USD base notice ── */}
        <motion.div variants={fadeUp} custom={2} initial="hidden" animate="visible"
          style={{ marginBottom:24, padding:"12px 18px", background:`${GOLD}08`, border:`1px solid ${GOLD}33`, display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:28, height:28, background:GOLD, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, color:PURE_W, fontWeight:700, flexShrink:0 }}>$</div>
          <div style={{ ...sans, fontSize:12, color:"#7A6520", lineHeight:1.6 }}>
            <strong>USD = 1.00 (base)</strong> — All rates are relative to 1 US Dollar. Example: EUR 0.92 means 1 USD = 0.92 EUR.
          </div>
        </motion.div>

        {/* ── Rate grid ── */}
        <motion.div variants={fadeUp} custom={3} initial="hidden" animate="visible"
          style={{ display:"grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap:2 }}>
          {filtered.map((c, i) => (
            <RateRow
              key={c.code}
              currency={c}
              value={rates[c.code] ?? c.default}
              isEdited={edited.has(c.code)}
              isBase={c.code === "USD"}
              onChange={val => handleChange(c.code, val)}
              index={i}
            />
          ))}
        </motion.div>

        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"60px 20px", color:WARMGRAY, ...sans, fontSize:13 }}>
            No currencies match "{search}"
          </div>
        )}

        {/* ── Bottom save ── */}
        {filtered.length > 6 && (
          <motion.div variants={fadeUp} custom={4} initial="hidden" animate="visible"
            style={{ marginTop:32, display:"flex", justifyContent:"flex-end" }}>
            <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:0.98 }}
              onClick={() => setConfirm(true)} disabled={loading}
              style={{ ...sans, padding:"14px 40px", border:`2px solid ${loading?STONE:NIGHT}`, background:loading?"#F8F6F3":NIGHT, color:loading?WARMGRAY:PURE_W, fontWeight:600, fontSize:12, cursor:loading?"wait":"pointer", letterSpacing:"0.18em", textTransform:"uppercase", transition:"all 0.3s" }}
              onMouseEnter={e => { if(!loading){ e.currentTarget.style.background="transparent"; e.currentTarget.style.color=NIGHT; } }}
              onMouseLeave={e => { if(!loading){ e.currentTarget.style.background=NIGHT; e.currentTarget.style.color=PURE_W; } }}>
              {loading ? "Saving…" : "Save All Rates"}
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* ── CONFIRM MODAL ── */}
      <AnimatePresence>
        {confirm && (
          <motion.div variants={modalOverlay} initial="hidden" animate="visible" exit="exit"
            onClick={() => setConfirm(false)}
            style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.65)", backdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
            <motion.div variants={modalContent} initial="hidden" animate="visible" exit="exit"
              onClick={e => e.stopPropagation()}
              style={{ background:PURE_W, width:"100%", maxWidth:480, padding:isMobile?24:40 }}>

              <h2 style={{ ...serif, fontSize:isMobile?26:32, fontWeight:400, color:NIGHT, margin:"0 0 12px", lineHeight:1.2 }}>
                Update {ALL_CURRENCIES.length} Rates?
              </h2>
              <p style={{ ...sans, fontSize:13, color:DUSK, lineHeight:1.7, margin:"0 0 24px" }}>
                This will overwrite all currency rates in Firestore immediately. All merchant apps using the currency SDK will receive the new rates within 24 hours (cache TTL).
              </p>

              {editedCount > 0 && (
                <div style={{ padding:"12px 16px", background:`${AMBER}08`, border:`1px solid ${AMBER}22`, ...sans, fontSize:12, color:"#7A6520", marginBottom:24 }}>
                  You've manually edited <strong>{editedCount}</strong> {editedCount === 1 ? "rate" : "rates"}.
                </div>
              )}

              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setConfirm(false)}
                  style={{ ...sans, flex:1, padding:"14px 0", border:`1.5px solid ${STONE}`, background:"transparent", color:DUSK, fontWeight:600, fontSize:11, cursor:"pointer", letterSpacing:"0.12em", textTransform:"uppercase", transition:"all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=NIGHT; e.currentTarget.style.color=NIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=STONE; e.currentTarget.style.color=DUSK; }}>
                  Cancel
                </button>
                <motion.button whileHover={{ scale:1.01 }} whileTap={{ scale:0.98 }}
                  onClick={handleSubmit}
                  style={{ ...sans, flex:2, padding:"14px 0", border:`2px solid ${NIGHT}`, background:NIGHT, color:PURE_W, fontWeight:600, fontSize:11, cursor:"pointer", letterSpacing:"0.16em", textTransform:"uppercase", transition:"all 0.3s" }}
                  onMouseEnter={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color=NIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.background=NIGHT; e.currentTarget.style.color=PURE_W; }}>
                  Confirm &amp; Save
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   RATE ROW — individual editable currency row
   ══════════════════════════════════════════════════ */
function RateRow({
  currency, value, isEdited, isBase, onChange, index,
}: {
  currency: typeof ALL_CURRENCIES[0];
  value:    number;
  isEdited: boolean;
  isBase:   boolean;
  onChange: (val: string) => void;
  index:    number;
}) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      custom={index * 0.3}
      variants={fadeUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:      "flex",
        alignItems:   "center",
        gap:          12,
        padding:      "14px 18px",
        background:   focused ? PURE_W : hovered ? PURE_W : isEdited ? `${AMBER}05` : IVORY,
        border:       `1px solid ${focused ? NIGHT : isEdited ? `${AMBER}44` : hovered ? STONE : "transparent"}`,
        transition:   "all 0.2s",
        borderRadius: 0,
      }}
    >
      {/* Symbol badge */}
      <div style={{
        width: 36, height: 36, background: isBase ? NIGHT : CREAM,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, fontSize: 13, fontWeight: 700,
        color: isBase ? PURE_W : DUSK,
        fontFamily: "'Courier New', monospace",
        letterSpacing: "-0.02em",
      }}>
        {currency.symbol.length > 2 ? currency.code.slice(0,2) : currency.symbol}
      </div>

      {/* Code + name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily:"'Libre Franklin','Helvetica Neue',sans-serif", fontSize: 13, fontWeight: 600, color: NIGHT, letterSpacing: "0.04em" }}>
          {currency.code}
          {isBase && (
            <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 600, letterSpacing: "0.14em", color: GOLD, textTransform: "uppercase", verticalAlign: "middle" }}>
              BASE
            </span>
          )}
          {isEdited && !isBase && (
            <span style={{ marginLeft: 8, fontSize: 9, fontWeight: 600, letterSpacing: "0.14em", color: AMBER, textTransform: "uppercase", verticalAlign: "middle" }}>
              EDITED
            </span>
          )}
        </div>
        <div style={{ fontFamily:"'Libre Franklin','Helvetica Neue',sans-serif", fontSize: 11, color: WARMGRAY, marginTop: 1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {currency.name}
        </div>
      </div>

      {/* Rate input */}
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <span style={{ fontFamily:"'Libre Franklin','Helvetica Neue',sans-serif", fontSize:11, color:WARMGRAY, letterSpacing:"0.06em" }}>1 USD =</span>
        <input
          type="number"
          value={value}
          min={0}
          step="any"
          disabled={isBase}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            fontFamily:  "'Courier New', monospace",
            width:       100,
            padding:     "8px 10px",
            border:      `1px solid ${focused ? NIGHT : STONE}`,
            background:  isBase ? CREAM : PURE_W,
            fontSize:    13,
            color:       isBase ? WARMGRAY : NIGHT,
            outline:     "none",
            textAlign:   "right",
            transition:  "border-color .15s",
            cursor:      isBase ? "not-allowed" : "text",
            boxShadow:   focused ? `0 0 0 2px ${NIGHT}10` : "none",
          }}
        />
      </div>
    </motion.div>
  );
}